import { useCallback, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import {
    PHO_FLOW,
    SignupRequirementsData,
} from '@perpay-web/authentication/constants/authenticationTypes';
import {
    PERPAY_PLUS_SOLO_APPLICATION,
    SIGNUP_PATH,
} from '@perpay-web/authentication/constants/paths';
import { REFRESHING } from '@perpay-web/authentication/constants/states';
import { setUserVerificationModalOpen } from '@perpay-web/components/actions/userVerificationModal';
import { useSignupRequirementsDataContext } from '@perpay-web/components/hooks/useSignupRequirementsDataContext';
import {
    CARD_PARTNER_ONBOARDING_APPLY,
    MARKETPLACE_PARTNER_ONBOARDING_APPLY,
    PAYROLL_CONNECTION_INTRO,
} from '@perpay-web/constants/paths';
import { stringifySearchParams } from '@perpay-web/utils/queryParamsUtils';

const PHO_FLOW_PATHS = [
    CARD_PARTNER_ONBOARDING_APPLY,
    MARKETPLACE_PARTNER_ONBOARDING_APPLY,
    PAYROLL_CONNECTION_INTRO,
    SIGNUP_PATH,
    PERPAY_PLUS_SOLO_APPLICATION,
];

const isOnPhoFlowPath = (path: string) =>
    PHO_FLOW_PATHS.some((p) => path.startsWith(p));

const getIsRefreshing = (state: { authentication: { tokenState: string } }) =>
    state.authentication.tokenState === REFRESHING;

export const useRedirectIfIncomplete = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [isLoadingRedirect, setIsLoadingRedirect] = useState(true);
    const isAuthenticationRefreshing = useSelector(getIsRefreshing);

    const {
        isLoadingOrUnrequested: isSignupDataLoading,
        data,
    }: {
        isLoading: boolean;
        data?: SignupRequirementsData;
        isLoadingOrUnrequested: boolean;
    } = useSignupRequirementsDataContext();

    const hasSecondaryRequirements = data?.requirements.secondary.length;
    const hasPrimaryRequirements = data?.requirements.primary.length;
    const onboardingPhoFlow = data?.latestPhoFlow;
    const continuePhoFlow = data?.continuePhoFlow;

    const navigateToPath = useCallback(
        (path: string, urlSearchParams: URLSearchParams) => {
            const search = stringifySearchParams(urlSearchParams);
            navigate({ pathname: path, search }, { replace: true });
        },
        [navigate],
    );

    useLayoutEffect(() => {
        if (isAuthenticationRefreshing || isSignupDataLoading) {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const phoComplete = params.get('PHO_complete') || false;
        const postLoginPath = params.get('next') || window.location.pathname;
        params.set('next', postLoginPath);

        // Priority 1: Handle new users with missing primary requirements
        if (hasPrimaryRequirements) {
            if (!phoComplete) {
                if (onboardingPhoFlow === PHO_FLOW.PAYCHEX) {
                    navigateToPath(PAYROLL_CONNECTION_INTRO, params);
                    return;
                }
                if (onboardingPhoFlow === PHO_FLOW.CARD) {
                    navigateToPath(CARD_PARTNER_ONBOARDING_APPLY, params);
                    return;
                }
                if (onboardingPhoFlow === PHO_FLOW.MARKETPLACE) {
                    navigateToPath(
                        MARKETPLACE_PARTNER_ONBOARDING_APPLY,
                        params,
                    );
                    return;
                }
            }
            navigateToPath(SIGNUP_PATH, params);
            return;
        }

        // Priority 2: Handle new and existing users with pho applications in progress
        if (continuePhoFlow && !isOnPhoFlowPath(location.pathname)) {
            if (!phoComplete) {
                if (continuePhoFlow === PHO_FLOW.PAYCHEX) {
                    navigateToPath(PAYROLL_CONNECTION_INTRO, params);
                    return;
                }
                if (continuePhoFlow === PHO_FLOW.CARD) {
                    navigateToPath(CARD_PARTNER_ONBOARDING_APPLY, params);
                    return;
                }
                // otherwise, this is marketplace
                navigateToPath(MARKETPLACE_PARTNER_ONBOARDING_APPLY, params);
                return;
            }
        }

        // Only show verification modal if not on a pho flow path
        if (hasSecondaryRequirements) {
            if (isOnPhoFlowPath(location.pathname)) {
                setIsLoadingRedirect(false);
                return;
            }
            dispatch(setUserVerificationModalOpen(true));
        }

        setIsLoadingRedirect(false);
    }, [
        dispatch,
        isAuthenticationRefreshing,
        isSignupDataLoading,
        location.pathname,
        navigate,
        hasSecondaryRequirements,
        onboardingPhoFlow,
        hasPrimaryRequirements,
        continuePhoFlow,
        navigateToPath,
    ]);

    return {
        isLoadingRedirect,
    };
};
